// src/components/multi-select.tsx

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  Plus,
  WandSparkles,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";


/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "transition-colors",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface MultiSelectGroup {
  label: string;
  options: MultiSelectOption[];
}

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options?: MultiSelectOption[];

  /**
   * Optional grouped options. When provided, options are displayed under
   * labeled group headings. Either `options` or `groups` must be supplied.
   */
  groups?: MultiSelectGroup[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * While nothing is selected, draws the trigger as a small dashed "+ Add" button.
   * Optional, defaults to false.
   */
  addStyle?: boolean;

  /**
   * The value of the selected options.
   * Optional, defaults to undefined.
   */
  value?: string[];
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options: optionsProp,
      groups,
      onValueChange,
      variant,
      defaultValue = [],
      value,
      placeholder = "Select options",
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      addStyle = false,
      ...props
    },
    ref
  ) => {
    const options = React.useMemo(
      () => (groups ? groups.flatMap((g) => g.options) : (optionsProp ?? [])),
      [groups, optionsProp]
    );
    const [internalSelectedValues, setInternalSelectedValues] =
      React.useState<string[]>(defaultValue);
    const selectedValues = value !== undefined ? value : internalSelectedValues;
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setInternalSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((val) => val !== option)
        : [...selectedValues, option];
      if (value !== undefined) {
        onValueChange(newSelectedValues);
      } else {
        setInternalSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const handleClear = () => {
      if (value !== undefined) {
        onValueChange([]);
      } else {
        setInternalSelectedValues([]);
        onValueChange([]);
      }
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      if (value !== undefined) {
        onValueChange(newSelectedValues);
      } else {
        setInternalSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        if (value !== undefined) {
          onValueChange(allValues);
        } else {
          setInternalSelectedValues(allValues);
          onValueChange(allValues);
        }
      }
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full min-h-10 h-auto items-center justify-between !rounded-aios-sm !border-[1.5px] !border-iai-border !bg-background !px-3.5 !py-2 !text-sm !font-normal !text-foreground !shadow-none hover:!bg-background [&_svg]:pointer-events-auto",
              addStyle &&
                selectedValues.length === 0 &&
                "h-8 min-h-8 w-fit !rounded-lg !border !border-dashed !border-[#D3CFE0] !px-3 !py-0 !text-xs !font-medium !text-muted-foreground hover:!text-foreground dark:!border-border",
              className
            )}
          >
            {selectedValues.length > 0 ? (
              <div className="flex w-full items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    const label = option?.label ?? "";
                    return (
                      <Badge
                        key={value}
                        className={cn(
                          "m-0 max-w-[min(160px,100%)]",
                          isAnimating ? "animate-bounce" : "",
                          multiSelectVariants({ variant })
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {IconComponent && (
                          <IconComponent className="mr-2 h-4 w-4 shrink-0" />
                        )}
                        <span className="min-w-0 truncate" title={label}>
                          {label}
                        </span>
                        <XCircle
                          className="ml-2 h-4 w-4 shrink-0 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      </Badge>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        "m-0 border-foreground/1 bg-transparent text-foreground hover:bg-transparent",
                        isAnimating ? "animate-bounce" : "",
                        multiSelectVariants({ variant })
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${selectedValues.length - maxCount} more`}
                      <XCircle
                        className="ml-2 h-4 w-4 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center self-center">
                  <XIcon
                    className="mx-2 h-4 cursor-pointer text-muted-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator
                    orientation="vertical"
                    className="flex h-full min-h-6"
                  />
                  <ChevronDown className="ml-2 h-4 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : addStyle ? (
              <span className="flex items-center gap-1.5">
                <Plus className="size-3.5 shrink-0" />
                {placeholder}
              </span>
            ) : (
              <div className="flex items-center justify-between gap-2 w-full mx-auto">
                <span className="text-sm text-muted-foreground">
                  {placeholder}
                </span>
                <ChevronDown className="h-4 shrink-0 cursor-pointer text-muted-foreground" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[max(280px,min(92vw,var(--radix-popover-trigger-width)))] overflow-hidden rounded-xl border border-border p-0 shadow-lg"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command className="rounded-xl">
            <CommandInput
              placeholder="Search..."
              onKeyDown={handleInputKeyDown}
              className="h-11"
            />
            <CommandList className="max-h-[240px] overscroll-contain scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup className="p-1.5">
                <CommandItem
                  key="all"
                  onSelect={toggleAll}
                  onMouseDown={(e) => e.preventDefault()}
                  className="cursor-pointer rounded-md px-2 py-2"
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border",
                      selectedValues.length === options.length &&
                        options.length > 0
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/40 bg-background"
                    )}
                  >
                    <CheckIcon
                      className={cn(
                        "h-3 w-3",
                        selectedValues.length === options.length &&
                          options.length > 0
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </div>
                  <span className="text-sm font-medium">Select all</span>
                </CommandItem>
              </CommandGroup>
              {groups ? (
                groups.map((group) => (
                  <CommandGroup
                    key={group.label}
                    heading={group.label}
                    className="p-1.5 pt-0"
                  >
                    {group.options.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          onSelect={() => toggleOption(option.value)}
                          onMouseDown={(e) => e.preventDefault()}
                          className="cursor-pointer min-w-0 rounded-md px-2 py-2"
                        >
                          <div
                            className={cn(
                              "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border",
                              isSelected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-muted-foreground/40 bg-background"
                            )}
                          >
                            <CheckIcon
                              className={cn(
                                "h-3 w-3",
                                isSelected ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </div>
                          {option.icon && (
                            <option.icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span
                            className="min-w-0 flex-1 truncate text-sm"
                            title={option.label}
                          >
                            {option.label}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))
              ) : (
                <CommandGroup className="p-1.5 pt-0">
                  {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        onSelect={() => toggleOption(option.value)}
                        onMouseDown={(e) => e.preventDefault()}
                        className="cursor-pointer min-w-0 rounded-md px-2 py-2"
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border",
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-muted-foreground/40 bg-background"
                          )}
                        >
                          <CheckIcon
                            className={cn(
                              "h-3 w-3",
                              isSelected ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </div>
                        {option.icon && (
                          <option.icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span
                          className="min-w-0 flex-1 truncate text-sm"
                          title={option.label}
                        >
                          {option.label}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
            <div className="flex items-center justify-between gap-2 border-t bg-muted/30 px-2 py-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground"
                disabled={selectedValues.length === 0}
                onClick={handleClear}
              >
                Clear
              </Button>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {selectedValues.length > 0
                  ? `${selectedValues.length} selected`
                  : "None selected"}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 px-2 text-xs font-medium"
                onClick={() => setIsPopoverOpen(false)}
              >
                Done
              </Button>
            </div>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              "cursor-pointer my-2 text-foreground bg-background w-3 h-3",
              isAnimating ? "" : "text-muted-foreground"
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
